import { useEffect, useRef } from 'react';
import { Megaphone, TriangleAlert } from 'lucide-react';
import type { AnnouncementTone, SystemAnnouncement } from '@forq/contracts';

const toneIcon: Record<AnnouncementTone, typeof Megaphone> = {
  info: Megaphone,
  warning: TriangleAlert,
  critical: TriangleAlert,
};

const toneLabel: Record<AnnouncementTone, string> = {
  info: 'Information',
  warning: 'Wichtiger Hinweis',
  critical: 'Achtung',
};

export interface AnnouncementDialogProps {
  readonly announcement: SystemAnnouncement;
  /** Hinweis unten links, z. B. „Dieser Hinweis erscheint bei jedem Start." */
  readonly note?: string;
  /**
   * Eingebettet statt ueber dem Bildschirm -- die Vorschau im Adminpanel. Dann kein Dialog: kein
   * aria-modal, kein Escape, kein Fokusklau. Eine Vorschau, die den Fokus stiehlt, waehrend jemand
   * den Text tippt, waere ein Fehler.
   */
  readonly embedded?: boolean;
  readonly onClose?: () => void;
}

/**
 * Das Fenster, das die Benutzer beim Start von FORQ sehen -- und dieselbe Komponente, die das
 * Adminpanel als Vorschau zeigt. Bewusst geteilt: eine nachgebaute Vorschau waere ab dem ersten
 * Stylefix eine Luege ueber das, was die Kollegen wirklich zu sehen bekommen.
 *
 * Bewusst ohne Fokus-Kaefig: Escape und ein Klick daneben schliessen. Wer Leute zwingt, eine
 * Meldung zu quittieren, bekommt keine Aufmerksamkeit, sondern einen Reflex -- und der trifft dann
 * auch die eine Meldung, auf die es ankommt.
 */
export function AnnouncementDialog({
  announcement,
  note,
  embedded = false,
  onClose,
}: AnnouncementDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (embedded) return undefined;
    confirmRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [embedded, onClose]);

  const Icon = toneIcon[announcement.tone];
  const headingId = `forq-announcement-title-${announcement.id}`;

  return (
    <div
      className={`forq-announcement__backdrop${embedded ? ' is-embedded' : ''}`}
      onClick={(event) => {
        if (!embedded && event.target === event.currentTarget) onClose?.();
      }}
    >
      <section
        className={`forq-announcement forq-announcement--${announcement.tone}`}
        role={embedded ? undefined : 'dialog'}
        aria-modal={embedded ? undefined : true}
        aria-labelledby={embedded ? undefined : headingId}
      >
        <header className="forq-announcement__head">
          <span className="forq-announcement__icon"><Icon size={20} /></span>
          <div>
            <span className="forq-eyebrow">{toneLabel[announcement.tone]}</span>
            <h2 id={headingId}>{announcement.title}</h2>
          </div>
        </header>
        {/* Absichtlich Text, nicht HTML: der Inhalt kommt aus einem Eingabefeld im Adminpanel.
            Wuerde er als Markup gerendert, waere das Startfenster JEDES Benutzers eine offene Tuer
            fuer alles, was ein Admin-Konto dort hineinschreibt. Umbrueche macht CSS (pre-wrap). */}
        <p className="forq-announcement__body">{announcement.body}</p>
        <footer className="forq-announcement__foot">
          {note ? <span className="forq-announcement__note">{note}</span> : null}
          <button
            className="forq-announcement__confirm"
            type="button"
            onClick={() => onClose?.()}
            ref={confirmRef}
            // In der Vorschau ist der Knopf Dekoration: er zeigt, wie das Fenster aussieht.
            // Nicht anklickbar, statt so zu tun, als passiere etwas.
            disabled={embedded}
          >
            Verstanden
          </button>
        </footer>
      </section>
    </div>
  );
}
